import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Chip,
  Paper
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import FilterListIcon from '@material-ui/icons/FilterList';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Filter operators configuration
const TEXT_OPERATORS = [
  { value: 'contains', label: 'מכיל' },
  { value: 'equals', label: 'שווה ל' },
  { value: 'startsWith', label: 'מתחיל ב' },
  { value: 'endsWith', label: 'מסתיים ב' },
  { value: 'isEmpty', label: 'ריק' },
  { value: 'isNotEmpty', label: 'לא ריק' },
  { value: 'isAnyOf', label: 'אחד מ' }
];

const OPERATORS_WITHOUT_VALUE = ['isEmpty', 'isNotEmpty'];

/**
 * MultiFilterPanel - Custom filter component that supports multiple filters
 * Replaces the built-in DataGrid single-filter limitation
 */
const MultiFilterPanel = ({
  filterItems,
  columns,
  rows,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onClearAllFilters,
  isSaving
}) => {
  // Get unique values for each column (for isAnyOf autocomplete)
  const columnValuesMap = useMemo(() => {
    const map = new Map();
    columns.forEach((col) => {
      if (col.filterable) {
        const values = new Set();
        rows.forEach((row) => {
          const value = row[col.field];
          if (value != null && value !== '') {
            values.add(String(value));
          }
        });
        map.set(col.field, Array.from(values).sort());
      }
    });
    return map;
  }, [columns, rows]);

  // Get available operators for a column
  const getOperatorsForColumn = (columnField) => {
    // For now, all columns use text operators
    // Can be extended later for numeric/date columns
    return TEXT_OPERATORS;
  };

  // Check if operator requires a value input
  const requiresValue = (operator) => {
    return !OPERATORS_WITHOUT_VALUE.includes(operator);
  };

  // Get column display name
  const getColumnLabel = (field) => {
    const column = columns.find((col) => col.field === field);
    return column ? column.headerName : field;
  };

  // Handle filter value change
  const handleValueChange = (filterId, newValue) => {
    onUpdateFilter(filterId, { value: newValue });
  };

  // Handle operator change
  const handleOperatorChange = (filterId, newOperator) => {
    // Clear value if switching to isEmpty/isNotEmpty
    const updates = { operatorValue: newOperator };
    if (OPERATORS_WITHOUT_VALUE.includes(newOperator)) {
      updates.value = '';
    } else if (newOperator === 'isAnyOf' && !Array.isArray(filterItems.find((f) => f.id === filterId)?.value)) {
      updates.value = [];
    }
    onUpdateFilter(filterId, updates);
  };

  // Handle column change
  const handleColumnChange = (filterId, newColumn) => {
    const currentFilter = filterItems.find((f) => f.id === filterId);
    const operators = getOperatorsForColumn(newColumn);
    const defaultOperator = operators[0]?.value || 'contains';
    
    onUpdateFilter(filterId, {
      columnField: newColumn,
      operatorValue: defaultOperator,
      value: defaultOperator === 'isAnyOf' ? [] : ''
    });
  };

  if (filterItems.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={onAddFilter}
          disabled={isSaving}
          size="small"
        >
          הוסף מסנן
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: '#f5f5f5'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FilterListIcon fontSize="small" />
          <Chip
            label={`${filterItems.length} מסננים פעילים`}
            size="small"
            color="primary"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddFilter}
            disabled={isSaving}
            size="small"
          >
            הוסף מסנן
          </Button>
          {filterItems.length > 0 && (
            <Button
              variant="outlined"
              onClick={onClearAllFilters}
              disabled={isSaving}
              size="small"
            >
              נקה הכל
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filterItems.map((filter) => {
          const operators = getOperatorsForColumn(filter.columnField);
          const needsValue = requiresValue(filter.operatorValue);
          const isAnyOfOperator = filter.operatorValue === 'isAnyOf';
          const columnValues = columnValuesMap.get(filter.columnField) || [];

          return (
            <Box
              key={filter.id}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                p: 1.5,
                backgroundColor: 'white',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                "& .MuiSelect-select, & .MuiInputBase-input": {
                  minHeight: 20
                },
                '& .MuiFormControl-root': {
                  marginRight: 8
                }
              }}
            >
              {/* Column Select */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel >עמודה</InputLabel>
                <Select
                  value={filter.columnField || ''}
                  onChange={(e) => handleColumnChange(filter.id, e.target.value)}
                  disabled={isSaving}
                  label="עמודה"
                  style={{ minWidth: 150, minHeight: 30 }}
                >
                  {columns
                    .filter((col) => col.filterable)
                    .map((col) => (
                      <MenuItem key={col.field} value={col.field}>
                        {col.headerName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Operator Select */}
              {filter.columnField && (
                <FormControl size="small" sx={{ minWidth: 120, marginRight: 1 }}>
                  <InputLabel>אופרטור</InputLabel>
                  <Select
                    value={filter.operatorValue || 'contains'}
                    onChange={(e) => handleOperatorChange(filter.id, e.target.value)}
                    disabled={isSaving}
                    label="אופרטור"
                  >
                    {operators.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Value Input */}
              {filter.columnField && needsValue && (
                <>
                  {isAnyOfOperator ? (
                    <Autocomplete
                      multiple
                      size="small"
                      options={columnValues}
                      value={Array.isArray(filter.value) ? filter.value : []}
                      onChange={(event, newValue) => {
                        handleValueChange(filter.id, newValue);
                      }}
                      disabled={isSaving}
                      sx={{ minWidth: 200, flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="ערכים"
                          placeholder="בחר ערכים"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  ) : (
                    <TextField
                      size="small"
                      label="ערך"
                      value={filter.value || ''}
                      onChange={(e) => handleValueChange(filter.id, e.target.value)}
                      disabled={isSaving}
                      sx={{ minWidth: 200, flex: 1 }}
                      placeholder="הכנס ערך לחיפוש"
                    />
                  )}
                </>
              )}

              {/* Delete Button */}
              <IconButton
                size="small"
                onClick={() => onRemoveFilter(filter.id)}
                disabled={isSaving}
                color="secondary"
                style={{ mt: 0.5, alignSelf: 'flex-end' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default MultiFilterPanel;
